import React from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
} from "@mui/material";
import { MultiChoiceAnswer, MultiChoiceQuestion } from "../../types";

interface MultiChoiceQuestionProps {
  question: MultiChoiceQuestion;
  value: MultiChoiceAnswer | undefined;
  onChange: (answer: MultiChoiceAnswer) => void;
}

export const MultiChoiceQuestionField: React.FC<MultiChoiceQuestionProps> = ({
  question,
  value,
  onChange,
}) => {
  const selections = value?.choices ?? [];
  const hasCustom = question.customOption
    ? selections.includes(question.customOption.value)
    : false;

  const toggleSelection = (choice: string) => {
    const exists = selections.includes(choice);
    const updated = exists
      ? selections.filter((item) => item !== choice)
      : [...selections, choice];

    onChange({
      choices: updated,
      customText:
        question.customOption && updated.includes(question.customOption.value)
          ? value?.customText ?? ""
          : undefined,
    });
  };

  const handleCustomChange = (text: string) => {
    if (!question.customOption) {
      return;
    }
    const choices = selections.includes(question.customOption.value)
      ? selections
      : [...selections, question.customOption.value];

    onChange({
      choices,
      customText: text,
    });
  };

  return (
    <FormControl component="fieldset" fullWidth>
      <FormGroup>
        <Stack spacing={1}>
          {question.options.map((option) => {
            const checked = selections.includes(option.value);
            return (
              <Card
                key={option.value}
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  borderColor: checked ? "primary.main" : undefined,
                  borderWidth: checked ? 2 : 1,
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
                onClick={() => toggleSelection(option.value)}
              >
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelection(option.value);
                        }}
                      />
                    }
                    label={
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        {option.icon && (
                          <Box
                            sx={{
                              mr: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {option.icon}
                          </Box>
                        )}
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle1">
                            {option.label}
                          </Typography>
                          {option.description && (
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelection(option.value);
                    }}
                    sx={{ m: 0, width: "100%", cursor: "pointer" }}
                  />
                </CardContent>
              </Card>
            );
          })}
          {question.customOption && (
            <Card
              key={question.customOption.value}
              variant="outlined"
              sx={{
                cursor: "pointer",
                borderColor: hasCustom ? "primary.main" : undefined,
                borderWidth: hasCustom ? 2 : 1,
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onClick={() => toggleSelection(question.customOption!.value)}
            >
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hasCustom}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelection(question.customOption!.value);
                        }}
                      />
                    }
                    label={
                      <Typography variant="subtitle1">
                        {question.customOption.label}
                      </Typography>
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelection(question.customOption!.value);
                    }}
                    sx={{ m: 0, width: "100%", cursor: "pointer" }}
                  />
                  {hasCustom && (
                    <TextField
                      fullWidth
                      value={value?.customText ?? ""}
                      onChange={(event) =>
                        handleCustomChange(event.target.value)
                      }
                      placeholder={question.customOption.placeholder}
                      helperText={question.customOption.helperText}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </FormGroup>
    </FormControl>
  );
};
